<template>
  <v-container fluid class="px-4 pt-4">
    <v-layout align-center class="mb-4">
      <v-flex>
        <h2 class="primary--text">{{ template ? template.title : "System email" }}</h2>
        <h4 class="pt-1 sub-header">{{ template ? template.description : "Account configuration" }}</h4>
      </v-flex>
      <v-btn color="secondary" @click="back()">Back to emails</v-btn>
    </v-layout>
    <v-alert :value="!!error" type="error" class="mb-3">{{ error }}</v-alert>
    <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
    <v-card v-if="template && !loading" class="pa-4">
      <v-text-field data-cy="system-email-subject" label="Subject" box v-model="subject" @input="hasChanged = true"></v-text-field>
      <p v-if="template.variables && template.variables.length" class="mb-2" data-cy="system-email-variables">
        Available values:
        <code v-for="variable in template.variables" :key="variable" class="mr-2">{{ formatVariable(variable) }}</code>
      </p>
      <p class="mb-4">Use three braces around a web address, for example <code>{{ linkExample }}</code>, so the link is inserted unchanged.</p>
      <v-layout row class="mb-2">
        <v-btn class="ml-0" color="secondary" @click="sourceMode = !sourceMode">
          {{ sourceMode ? "Visual editor" : "HTML source" }}
        </v-btn>
      </v-layout>
      <wysiwyg v-if="!sourceMode" v-model="body" placeholder="Email HTML"></wysiwyg>
      <v-textarea
        v-else
        data-cy="system-email-html"
        v-model="body"
        box
        auto-grow
        rows="16"
        placeholder="Email HTML">
      </v-textarea>
      <v-layout row class="mt-4">
        <v-btn data-cy="system-email-save" class="ml-0" color="success" :disabled="!hasChanged || saving" :loading="saving" @click="save()">Save</v-btn>
        <v-btn data-cy="system-email-preview" color="secondary" :disabled="previewing" @click="preview()">Preview</v-btn>
        <v-btn data-cy="system-email-restore" color="error" :disabled="saving" @click="confirmReset = true">Restore default</v-btn>
      </v-layout>
      <div v-if="previewHtml" class="mt-4">
        <h3 class="mb-2">Preview</h3>
        <iframe
          data-cy="system-email-preview-frame"
          style="width: 100%; min-height: 420px; border: 1px solid #AAAAAA;"
          frameborder="0"
          :srcdoc="previewHtml">
        </iframe>
      </div>
    </v-card>
    <v-dialog v-model="confirmReset" max-width="480">
      <v-card class="pa-4">
        <v-card-title class="pa-0 pb-2"><h2>Restore the built-in email?</h2></v-card-title>
        <p>This replaces the subject and HTML for this account with the original Caseblocks format.</p>
        <v-card-actions class="pa-0">
          <v-btn data-cy="system-email-restore-confirm" color="error" @click="restore()">Restore</v-btn>
          <v-btn @click="confirmReset = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { provider } from "@/provider.js"

export default {
  name: "system-email-template",
  data: () => ({
    template: null,
    subject: "",
    body: "",
    loading: false,
    saving: false,
    previewing: false,
    hasChanged: false,
    sourceMode: true,
    error: "",
    previewHtml: "",
    confirmReset: false,
    linkExample: "{{{link}}}"
  }),
  computed: {
    emailId() {
      const route = this.$route
      if (!route || !route.params) return undefined
      return route.params.email_id
    }
  },
  watch: {
    body() {
      this.hasChanged = true
    },
    emailId() {
      this.load()
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    back() {
      this.$router.push({ name: "AccountConfig", params: { account_tab: "emails" } })
    },
    formatVariable(variable) {
      return "{{" + variable + "}}"
    },
    applyTemplate(template) {
      this.template = template
      this.subject = template.subject || ""
      this.body = template.template || ""
      this.previewHtml = ""
      this.$nextTick(() => {
        this.hasChanged = false
      })
    },
    async load() {
      this.loading = true
      this.error = ""
      try {
        const templates = await this.$store.dispatch("systemEmailTemplates")
        const match = (templates || []).find((item) => parseInt(item.id, 10) === parseInt(this.emailId, 10))
        if (!match) {
          this.error = "That system email could not be found."
          this.template = null
        } else {
          this.applyTemplate(match)
        }
      } catch (error) {
        this.error = "Unable to load this system email."
      }
      this.loading = false
    },
    async save() {
      this.saving = true
      this.error = ""
      const response = await this.$store.dispatch("updateSystemEmailTemplate", {
        id: parseInt(this.template.id, 10),
        subject: this.subject,
        template: this.body
      })
      this.saving = false
      if (response && response.error) {
        this.error = response.message || "Unable to update system email."
        return
      }
      if (response && response.template) {
        this.applyTemplate(response.template)
      } else {
        this.hasChanged = false
      }
    },
    async preview() {
      this.previewing = true
      this.error = ""
      let payload = {}
      try {
        payload = JSON.parse(this.template.sampleInput || "{}")
      } catch (error) {
        payload = {}
      }
      const response = await provider.renderEmailTemplate({ id: this.template.id, payload: payload })
      this.previewing = false
      if (!response || response.error) {
        this.error = (response && response.message) || "Unable to preview this email."
        return
      }
      this.previewHtml = response.rendering || ""
    },
    async restore() {
      this.confirmReset = false
      this.saving = true
      this.error = ""
      const response = await this.$store.dispatch("resetSystemEmailTemplate", parseInt(this.template.id, 10))
      this.saving = false
      if (response && response.error) {
        this.error = response.message || "Unable to restore this email."
        return
      }
      if (response && response.template) {
        this.applyTemplate(response.template)
      }
    }
  }
}
</script>
